import { Link, useNavigate } from 'react-router-dom';
import { goBackOrFallback } from '../utils/navigationBack';
import { Compass, ArrowLeft, LayoutDashboard, MessageSquare, Search } from 'lucide-react';

/**
 * Friendly 404 — replaces the silent bounce-to-dashboard that used to happen
 * when a logged-in user hit an unknown path. Gives the user a clear "page
 * doesn't exist" signal plus three useful destinations.
 */
export default function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-[70vh] bg-theme flex items-center justify-center px-4 py-16">
      <div className="max-w-2xl w-full text-center" data-testid="not-found-page">
        <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-theme-primary/10 mb-6">
          <Compass className="h-10 w-10 text-theme-primary" />
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-theme-text tracking-tight mb-3">
          Page not found
        </h1>
        <p className="text-base text-theme-muted max-w-md mx-auto mb-10">
          We can't find the page you're looking for. It might have moved, been
          renamed, or never existed.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl mx-auto mb-10">
          <Link
            to="/dashboard"
            className="flex flex-col items-center gap-2 p-4 rounded-xl border border-theme bg-theme-secondary hover:border-theme-primary/40 hover:bg-theme-primary/5 transition-colors group"
            data-testid="not-found-go-dashboard"
          >
            <LayoutDashboard className="h-5 w-5 text-theme-muted group-hover:text-theme-primary transition-colors" />
            <span className="text-sm font-medium text-theme-text">Dashboard</span>
          </Link>
          <Link
            to="/casting-calls"
            className="flex flex-col items-center gap-2 p-4 rounded-xl border border-theme bg-theme-secondary hover:border-theme-primary/40 hover:bg-theme-primary/5 transition-colors group"
            data-testid="not-found-go-casting"
          >
            <Search className="h-5 w-5 text-theme-muted group-hover:text-theme-primary transition-colors" />
            <span className="text-sm font-medium text-theme-text">Browse Casting Calls</span>
          </Link>
          <Link
            to="/messages"
            className="flex flex-col items-center gap-2 p-4 rounded-xl border border-theme bg-theme-secondary hover:border-theme-primary/40 hover:bg-theme-primary/5 transition-colors group"
            data-testid="not-found-go-messages"
          >
            <MessageSquare className="h-5 w-5 text-theme-muted group-hover:text-theme-primary transition-colors" />
            <span className="text-sm font-medium text-theme-text">Messages</span>
          </Link>
        </div>

        <button
          onClick={() => goBackOrFallback(navigate, '/')}
          className="inline-flex items-center gap-2 text-sm text-theme-muted hover:text-theme-text transition-colors"
          data-testid="not-found-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Go back
        </button>
      </div>
    </div>
  );
}
